import { useState, useEffect, useId, useRef, createContext, useContext } from "react";
import PropTypes from "prop-types";
import Icon from "./Icon";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
export { default as Icon } from "./Icon";

export const PageTitle = ({ children, icon = "document", level = 1 }) => {
  const Heading = `h${level}`;
  return <Heading className="rf-page-title" data-page-icon={icon}><span>{children}</span></Heading>;
};
PageTitle.propTypes = { children: PropTypes.node, icon: PropTypes.string, level: PropTypes.oneOf([1, 2]) };

export const EmptyState = ({ title, children, icon = "document" }) => <div className="rf-empty"><Icon name={icon} size={28} /><strong>{title}</strong>{children}</div>;
EmptyState.propTypes = { title: PropTypes.string, children: PropTypes.node, icon: PropTypes.string };
import { REGIAO_COLORS } from "../constants";
import { useTenantGeography } from "../hooks/useTenantGeography";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useTenant } from "../context/TenantContext";
import { getDestinoLabel } from "../utils/tenantLabels";

export const Toast = ({ children, tone = "success", onDismiss, inline = false, ...props }) => (
  <div {...props} className={`rf-toast rf-toast-${tone}${inline ? " rf-toast-inline" : ""}`}>
    <div className="rf-toast-content" role={tone === "error" ? "alert" : "status"} aria-atomic="true">
      <Icon name={tone === "error" ? "warning" : "check"} size={20} /><span>{children}</span>
    </div>
    {onDismiss && <button type="button" className="rf-toast-dismiss" aria-label="Fechar aviso" onClick={onDismiss}><Icon name="close" size={16} /></button>}
  </div>
);
Toast.propTypes = { children: PropTypes.node, tone: PropTypes.oneOf(["error", "success"]), onDismiss: PropTypes.func, inline: PropTypes.bool };

export const Spinner = () => (
  <div style={{ width: 18, height: 18, border: "2px solid rgba(249,115,22,0.3)", borderTopColor: "#f97316", borderRadius: "50%" }} className="spin" role="status" aria-label="Carregando" />
);

export const Input = ({ label, error, hint, id, onFocus, onBlur, endAdornment, wrapperStyle, ...props }) => {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const describedBy = [props["aria-describedby"], hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, ...wrapperStyle }}>
      {label && <label className="rf-field-label" data-required={props.required && !String(label).includes("*") ? true : undefined} htmlFor={fieldId} style={{ fontSize: '0.9375rem', color: error ? "var(--danger)" : "var(--muted)", fontWeight: 500 }}>{label}</label>}
      <div className="rf-input-wrap"><input {...props} className={`rf-field-control ${props.className || ""}`} id={fieldId} required={props.required ?? (typeof label === "string" && label.includes("*"))} aria-required={(props.required ?? (typeof label === "string" && label.includes("*"))) || undefined} aria-invalid={error ? true : undefined} aria-describedby={describedBy}
        style={{ background: "var(--s1)", border: `1px solid ${error ? "var(--danger)" : "var(--control-border)"}`, borderRadius: 6, padding: "10px 12px", minHeight: 48, maxWidth: "100%", color: "var(--text)", width: "100%", transition: "border-color 0.2s", ...props.style }}
        onFocus={e => { e.target.style.borderColor = error ? "var(--danger)" : "var(--accent)"; onFocus?.(e); }}
        onBlur={e => { e.target.style.borderColor = error ? "var(--danger)" : "var(--control-border)"; onBlur?.(e); }}
      />{endAdornment && <span className="rf-input-end">{endAdornment}</span>}</div>
      {hint && <span id={hintId} className="rf-field-hint">{hint}</span>}
      {error && <span id={errorId} role="alert" style={{ fontSize: '0.875rem', color: "var(--danger)", marginTop: -2 }}><Icon name="warning" size={14} /> {error}</span>}
    </div>
  );
};

export const Select = ({ label, error, hint, children, id, wrapperStyle, ...props }) => {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const describedBy = [props["aria-describedby"], hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, ...wrapperStyle }}>
      {label && <label className="rf-field-label" data-required={props.required && !String(label).includes("*") ? true : undefined} htmlFor={fieldId} style={{ fontSize: '0.9375rem', color: error ? "var(--danger)" : "var(--muted)", fontWeight: 500 }}>{label}</label>}
      <select {...props} className={`rf-field-control ${props.className || ""}`} id={fieldId} required={props.required ?? (typeof label === "string" && label.includes("*"))} aria-required={(props.required ?? (typeof label === "string" && label.includes("*"))) || undefined} aria-invalid={error ? true : undefined} aria-describedby={describedBy}
        style={{ background: "var(--s1)", border: `1px solid ${error ? "var(--danger)" : "var(--control-border)"}`, borderRadius: 6, padding: "10px 12px", minHeight: 48, maxWidth: "100%", color: "var(--text)", width: "100%", ...props.style }}>{children}</select>
      {hint && <span id={hintId} className="rf-field-hint">{hint}</span>}
      {error && <span id={errorId} role="alert" style={{ fontSize: '0.875rem', color: "var(--danger)", marginTop: -2 }}><Icon name="warning" size={14} /> {error}</span>}
    </div>
  );
};

export const Btn = ({ children, variant = "primary", size = "md", type = "button", onMouseEnter, onMouseLeave, onMouseDown, onMouseUp, ...props }) => {
  const isLink = Boolean(props.href);
  const Control = isLink ? 'a' : 'button';
  const styles = {
    primary: { background: "var(--accent)", color: "var(--on-accent)" },
    secondary: { background: "var(--s2)", color: "var(--text)", border: "1px solid var(--control-border)" },
    danger: { background: "rgba(239,68,68,0.15)", color: "var(--danger)", border: "1px solid #b46368" },
    ghost: { background: "transparent", color: "var(--muted)", border: "1px solid transparent" },
  };
  const sizes = {
    sm: { padding: "8px 12px", fontSize: '0.9375rem' },
    md: { padding: "9px 16px", fontSize: '1rem' },
    lg: { padding: "12px 24px", fontSize: '1rem', fontWeight: 600 },
  };
  return (
    <Control {...props} disabled={isLink ? undefined : props.disabled}
      {...(isLink ? {'aria-disabled':props.disabled || undefined,tabIndex:props.disabled ? -1 : props.tabIndex} : {})}
      className={`rf-btn rf-btn-${variant} ${props.className || ""}`} type={isLink ? undefined : type} style={{ minHeight: 48, minWidth: 48, cursor: props.disabled ? "not-allowed" : "pointer", opacity: props.disabled ? 0.7 : 1, borderRadius: 6, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6, textDecoration:'none', transition: "background-color 0.15s, border-color 0.15s", ...styles[variant], ...sizes[size], ...props.style }}
      onClick={event => {
        if (isLink) {
          if (props.disabled) { event.preventDefault(); return; }
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (props.onClick) event.preventDefault();
        }
        props.onClick?.(event);
      }}
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onMouseDown={onMouseDown} onMouseUp={onMouseUp}
    >{children}</Control>
  );
};

const ModalContext = createContext(null);

const modalStack = [];
let previousBodyOverflow = "";
const focusableElements = (panel) => Array.from(panel.querySelectorAll(
  'button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
)).filter(element => element.getClientRects().length > 0 && !element.closest('[inert]'));

export const Modal = ({ title, onClose, children, maxWidth = 480, actions, closeLabel = "Fechar janela" }) => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const titleId = useId();
  const panelRef = useRef(null);
  const closeGuard = useRef(null);
  const requestClose = () => closeGuard.current ? closeGuard.current(onClose) : onClose();
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;

  useEffect(() => {
    const previousFocus = document.activeElement;
    if (modalStack.length === 0) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    modalStack.push(titleId);
    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel || modalStack.at(-1) !== titleId) return;
      // Não roubar o foco se a pessoa já selecionou um campo do modal.
      if (panel.contains(document.activeElement)) return;
      const firstField = panel.querySelector('input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])');
      (firstField || panel).focus();
    });
    const handleKey = (event) => {
      if (modalStack.at(-1) !== titleId) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      }
      if (event.key === "Tab") {
        const panel = panelRef.current;
        if (!panel) return;
        const items = focusableElements(panel);
        const first = items[0];
        const last = items.at(-1);
        if (!first) { event.preventDefault(); panel.focus(); return; }
        if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKey);
      const wasTopmost = modalStack.at(-1) === titleId;
      const index = modalStack.indexOf(titleId);
      if (index !== -1) modalStack.splice(index, 1);
      if (modalStack.length === 0) document.body.style.overflow = previousBodyOverflow;
      if (wasTopmost && previousFocus?.isConnected) previousFocus.focus();
    };
  }, [titleId]);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby={titleId}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: isMobile ? 12 : 20 }}
      onClick={event => { if (event.target === event.currentTarget && modalStack.at(-1) === titleId) requestClose(); }}>
      <div ref={panelRef} tabIndex={-1}
        style={{ background: "var(--s1)", border: "1px solid var(--border)", borderRadius: 10, width: "100%", maxWidth, maxHeight: "90dvh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        className="rf-modal-panel">
        <div className="rf-modal-header">
          <h3 id={titleId} style={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 700 }}>{title}</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>{actions}
          <button type="button" aria-label={closeLabel} onClick={requestClose}
            style={{ minWidth: 48, minHeight: 48, flexShrink: 0, background: "none", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontSize: '1.375rem', cursor: "pointer", lineHeight: 1 }}><Icon name="close" size={18} /></button>
          </div>
        </div>
        <ModalContext.Provider value={{ requestClose, closeGuard }}>
          <div className="rf-modal-body">{children}</div>
        </ModalContext.Provider>
      </div>
    </div>
  );
};

export const ConfirmModal = ({
  open,
  title,
  message,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  confirmVariant = "primary",
  loading = false,
  confirmDisabled = false,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;

  return (
    <Modal title={title} onClose={loading ? () => { } : onClose}>
      <div style={{ display: "grid", gap: 22 }}>
        {typeof message === "string" ? (
          <p style={{ color: "var(--muted)", fontSize: '1rem', lineHeight: 1.6 }}>{message}</p>
        ) : (
          <div style={{ color: "var(--muted)", fontSize: '1rem', lineHeight: 1.6 }}>{message}</div>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={onClose} variant="secondary" style={{ flex: 1, justifyContent: "center" }} disabled={loading}>
            {cancelLabel}
          </Btn>
          <Btn onClick={onConfirm} variant={confirmVariant} style={{ flex: 1, justifyContent: "center" }} disabled={loading || confirmDisabled}>
            {loading ? <><Spinner /> Processando…</> : confirmLabel}
          </Btn>
        </div>
      </div>
    </Modal>
  );
};

export const FormSection = ({ title, children }) => (
  <fieldset style={{ border: 0, borderTop: "1px solid var(--border)", padding: "12px 0 0", margin: 0, minWidth: 0, display: "grid", gap: 14 }}>
    <legend style={{ paddingRight: 10, fontSize: '1rem', fontWeight: 600, color: "var(--text)" }}>{title}</legend>
    {children}
  </fieldset>
);

export const Card = ({ children, style, onClick, surface = "panel", className = "", ...props }) => (
  <div {...props} className={`rf-card ${className}`} data-surface={surface} style={{ ...(surface === "filter" ? {} : { background: "var(--s1)", border: "1px solid var(--border-subtle)", borderRadius: 8, padding: 20 }), ...style }} onClick={onClick}>{children}</div>
);

export const RegiaoBadge = ({ regiao }) => {
  const { regioes_bairros: REGIOES_BAIRROS } = useTenantGeography();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const detailsId = useId();
  const s = REGIAO_COLORS[regiao] || REGIAO_COLORS["Sem região"];
  const bairros = REGIOES_BAIRROS[regiao] || [];

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const handleKey = event => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", handleOutside);
    document.addEventListener("keydown", handleKey);
    return () => { document.removeEventListener("pointerdown", handleOutside); document.removeEventListener("keydown", handleKey); };
  }, [open]);

  if (bairros.length === 0) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: '0.8125rem', fontWeight: 600, background: s.bg, color: s.color }}>
        {regiao}
      </span>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button type="button" aria-expanded={open} aria-controls={detailsId}
        onClick={() => setOpen(o => !o)}
        style={{ display: "inline-flex", alignItems: "center", padding: "8px 12px", borderRadius: 6, fontSize: '0.9375rem', fontWeight: 600, background: s.bg, color: s.color, cursor: "pointer", userSelect: "none", border: "1px solid var(--control-border)", minHeight: 48 }}
      >
        {regiao} <Icon name="chevron-down" size={16} />
      </button>
      {open && (
        <div id={detailsId} style={{ position: "absolute", top: "110%", left: 0, background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", fontSize: '0.875rem', color: "var(--text)", whiteSpace: "normal", width: "max-content", maxWidth: "min(320px, calc(100vw - 48px))", zIndex: 50, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: s.color }}>Bairros desta região:</div>
          {bairros.map(b => <div key={b} style={{ color: "var(--muted)" }}>• {b}</div>)}
        </div>
      )}
    </div>
  );
};

export const Pagination = ({ page, total, perPage, onChange }) => {
  const compact = useMediaQuery("(max-width: 600px)");
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  // Gera range de páginas visíveis: até 5 botões ao redor da atual
  const pages = [];
  const delta = 2;
  const left = Math.max(1, page - delta);
  const right = Math.min(totalPages, page + delta);
  for (let i = left; i <= right; i++) pages.push(i);

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "14px 16px", borderTop: "1px solid var(--border)" }}>
      <span style={{ fontSize: '0.9375rem', color: "var(--muted)" }}>
        {from}–{to} de <strong style={{ color: "var(--text)" }}>{total}</strong>
      </span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button
          type="button" aria-label="Página anterior" onClick={() => onChange(page - 1)}
          disabled={page === 1}
          style={{ minWidth: 48, minHeight: 48, padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--s2)", color: page === 1 ? "var(--muted)" : "var(--text)", cursor: page === 1 ? "not-allowed" : "pointer", fontSize: '1rem', opacity: page === 1 ? 0.4 : 1 }}
        >‹</button>

        {!compact && left > 1 && (
          <>
            <button onClick={() => onChange(1)} style={{ minWidth: 48, minHeight: 48, padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--s2)", color: "var(--text)", cursor: "pointer", fontSize: '0.9375rem' }}>1</button>
            {left > 2 && <span style={{ color: "var(--muted)", fontSize: '0.9375rem', padding: "0 2px" }}>…</span>}
          </>
        )}

        {compact && <span className="rf-pagination-position">Página {page} de {totalPages}</span>}
        {!compact && pages.map(p => (
          <button key={p} type="button" aria-label={`Página ${p}`} aria-current={p === page ? "page" : undefined} onClick={() => onChange(p)} style={{ minWidth: 48, minHeight: 48, padding: "5px 10px", borderRadius: 6, border: p === page ? "1px solid var(--accent)" : "1px solid var(--border)", background: p === page ? "rgba(249,115,22,0.15)" : "var(--s2)", color: p === page ? "var(--accent)" : "var(--text)", cursor: "pointer", fontSize: '0.9375rem', fontWeight: p === page ? 700 : 400 }}>{p}</button>
        ))}

        {!compact && right < totalPages && (
          <>
            {right < totalPages - 1 && <span style={{ color: "var(--muted)", fontSize: '0.9375rem', padding: "0 2px" }}>…</span>}
            <button onClick={() => onChange(totalPages)} style={{ minWidth: 48, minHeight: 48, padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--s2)", color: "var(--text)", cursor: "pointer", fontSize: '0.9375rem' }}>{totalPages}</button>
          </>
        )}

        <button
          type="button" aria-label="Próxima página" onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          style={{ minWidth: 48, minHeight: 48, padding: "5px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--s2)", color: page === totalPages ? "var(--muted)" : "var(--text)", cursor: page === totalPages ? "not-allowed" : "pointer", fontSize: '1rem', opacity: page === totalPages ? 0.4 : 1 }}
        >›</button>
      </div>
    </div>
  );
};

// TrechoToggle responsivo: padding menor no mobile para caber na tela
export const TrechoToggle = ({ trecho, setTrecho, destinoLabel }) => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { config } = useTenant();
  const destino = destinoLabel || getDestinoLabel(config);
  return (
    <div style={{ display: "flex", background: "var(--s2)", borderRadius: 10, padding: 4, border: "1px solid var(--border)", gap: 4 }}>
      {[
        { value: "ida", label: `Ida → ${destino}`, desc: "Buscar funcionários" },
        { value: "volta", label: "Volta → Casa", desc: "Retorno para casa" },
      ].map(opt => (
        <button key={opt.value} type="button" aria-pressed={trecho === opt.value} onClick={() => setTrecho(opt.value)} style={{
          flex: 1,
          minHeight: 48,
          padding: isMobile ? "8px 12px" : "10px 20px",
          borderRadius: 8, border: "none", cursor: "pointer",
          background: trecho === opt.value ? "var(--accent)" : "transparent",
          color: trecho === opt.value ? "var(--on-accent)" : "var(--muted)",
          fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: isMobile ? 13 : 14,
          transition: "background-color 0.2s, color 0.2s", display: "flex", flexDirection: "column", alignItems: "center", gap: 2
        }}>
          <span>{opt.label}</span>
          {!isMobile && <span style={{ fontSize: '0.8125rem', fontWeight: 400, opacity: 0.8 }}>{opt.desc}</span>}
        </button>
      ))}
    </div>
  );
};

const fieldPropTypes = {
  label: PropTypes.node, error: PropTypes.node, id: PropTypes.string, className: PropTypes.string,
  style: PropTypes.object, 'aria-describedby': PropTypes.string,
};
Input.propTypes = {
  hint: PropTypes.string, endAdornment: PropTypes.node, ...fieldPropTypes, onFocus: PropTypes.func, onBlur: PropTypes.func };
Select.propTypes = {
  hint: PropTypes.string, ...fieldPropTypes, children: PropTypes.node };
Btn.propTypes = {
  children: PropTypes.node, variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']), type: PropTypes.string,
  disabled: PropTypes.bool, style: PropTypes.object, className: PropTypes.string,
  href: PropTypes.string, tabIndex: PropTypes.number, onClick: PropTypes.func,
  onMouseEnter: PropTypes.func, onMouseLeave: PropTypes.func, onMouseDown: PropTypes.func, onMouseUp: PropTypes.func,
};
Modal.propTypes = { title: PropTypes.node, onClose: PropTypes.func.isRequired, children: PropTypes.node, maxWidth: PropTypes.number, actions: PropTypes.node, closeLabel: PropTypes.string };
ConfirmModal.propTypes = {
  open: PropTypes.bool, title: PropTypes.node, message: PropTypes.node, confirmLabel: PropTypes.node,
  cancelLabel: PropTypes.node, confirmVariant: PropTypes.string, loading: PropTypes.bool, confirmDisabled: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired,
};
Card.propTypes = { className: PropTypes.string, surface: PropTypes.oneOf(["panel", "filter"]), children: PropTypes.node, style: PropTypes.object, onClick: PropTypes.func };
RegiaoBadge.propTypes = { regiao: PropTypes.string };
Pagination.propTypes = { page: PropTypes.number, total: PropTypes.number, perPage: PropTypes.number, onChange: PropTypes.func.isRequired };
TrechoToggle.propTypes = { trecho: PropTypes.string, setTrecho: PropTypes.func.isRequired, destinoLabel: PropTypes.string };

FormSection.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

const formSnapshot = form => JSON.stringify(Array.from(form?.querySelectorAll('input,select,textarea') || []).map(field =>
  [field.id, field.type === 'checkbox' || field.type === 'radio' ? field.checked : field.value]));

export const Form = ({ onSubmit, children, values, busy = false, protect = true, className = '', ...props }) => {
  const ref = useRef(null);
  const baseline = useRef('');
  const [domDirty, setDomDirty] = useState(false);
  const initialValues = useRef(JSON.stringify(values));
  const dirty = values === undefined ? domDirty : JSON.stringify(values) !== initialValues.current;
  const [pendingLeave, setPendingLeave] = useState(null);
  const modal = useContext(ModalContext);
  const requestLeave = action => { if (busy) return; if (dirty && protect) setPendingLeave({ action }); else action(); };
  useEffect(() => { baseline.current = formSnapshot(ref.current); }, []);
  useEffect(() => {
    if (!modal || !protect) return undefined;
    modal.closeGuard.current = requestLeave;
    return () => { modal.closeGuard.current = null; };
  });
  useUnsavedChanges(dirty && protect, requestLeave);
  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    await onSubmit();
    requestAnimationFrame(() => {
      const error = ref.current?.querySelector('[aria-invalid="true"], [role="alert"]');
      if (error) { if (!error.matches('input,select,textarea,button')) error.tabIndex = -1; error.focus(); }
    });
  };
  return <>
    <form {...props} ref={ref} className={`rf-form ${className}`} onSubmit={submit}
      onChangeCapture={() => setDomDirty(formSnapshot(ref.current) !== baseline.current)}>{children}</form>
    <ConfirmModal open={Boolean(pendingLeave)} title="Descartar alterações?" message="O preenchimento ainda não foi salvo."
      confirmLabel="Descartar alterações" cancelLabel="Continuar editando" confirmVariant="danger"
      onClose={() => setPendingLeave(null)} onConfirm={() => { const action = pendingLeave.action; setPendingLeave(null); action(); }} />
  </>;
};
Form.propTypes = { onSubmit: PropTypes.func.isRequired, children: PropTypes.node, values: PropTypes.object, busy: PropTypes.bool, protect: PropTypes.bool, className: PropTypes.string };
export const FormCancel = ({ children = 'Cancelar', ...props }) => {
  const modal = useContext(ModalContext);
  return <Btn {...props} variant="secondary" onClick={modal?.requestClose}>{children}</Btn>;
};
FormCancel.propTypes = { children: PropTypes.node };
